import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Send, Settings, FileText, AlertTriangle, CheckCircle2, Paperclip } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import api from "../../lib/api";

interface EmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  kind: "invoices" | "quotes";
  docId: string;
  docNumber: string;
  contactName?: string;
  contactEmail?: string;
  companyName?: string;
  onSent?: () => void;
}

interface MailConfigInfo {
  configured: boolean;
  fromName?: string;
  fromEmail?: string;
}

const NL_TYPE: Record<string, string> = { invoices: "factuur", quotes: "offerte" };

export function EmailModal({
  isOpen,
  onClose,
  kind,
  docId,
  docNumber,
  contactName,
  contactEmail,
  companyName,
  onSent,
}: EmailModalProps) {
  const navigate = useNavigate();
  const [mailInfo, setMailInfo] = useState<MailConfigInfo | null>(null);
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachHtml, setAttachHtml] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [notConfigured, setNotConfigured] = useState(false);

  const typeLabel = NL_TYPE[kind] || "document";

  const buildConcept = (recipient: string) => {
    const greeting = recipient
      ? `Beste ${recipient},`
      : "Beste,";
    return (
      `${greeting}\n\n` +
      `In de bijlage van deze e-mail vindt u uw ${typeLabel} ${docNumber}.\n` +
      `U kunt het bijgevoegde bestand openen in uw browser en eventueel afdrukken naar PDF.\n\n` +
      `Heeft u vragen over deze ${typeLabel}? Neem dan gerust contact met ons op.\n\n` +
      `Met vriendelijke groet,\n\n` +
      `${companyName || ""}`
    );
  };

  useEffect(() => {
    if (!isOpen) return;
    setError("");
    setSuccess("");
    setNotConfigured(false);
    setTo(contactEmail || "");
    setCc("");
    setBcc("");
    setSubject(`${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)} ${docNumber}`);
    setBody(buildConcept(contactName || ""));
    setAttachHtml(true);

    api
      .get("/api/mail-config")
      .then((res) => setMailInfo(res.data))
      .catch(() => setMailInfo({ configured: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, docId, docNumber, contactEmail, contactName, companyName]);

  const handleSend = async () => {
    setError("");
    setSuccess("");
    setSending(true);
    setNotConfigured(false);
    try {
      const { data } = await api.post(`/api/${kind}/${docId}/email`, {
        to,
        cc: cc || undefined,
        bcc: bcc || undefined,
        subject,
        body,
        attachHtml,
      });
      setSuccess(`Verstuurd!${data.shareLink ? " Online link: " + data.shareLink : ""}`);
      onSent?.();
      setTimeout(() => onClose(), 1800);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 428) {
        setNotConfigured(true);
        setError(err?.response?.data?.error || "Mail is niet geconfigureerd voor dit bedrijf.");
      } else {
        setError(err?.response?.data?.error || "Versturen mislukt.");
      }
    } finally {
      setSending(false);
    }
  };

  // Fallback: markeer als verstuurd zonder te mailen (zelfde als oude /send of status PATCH).
  const handleMarkSentOnly = async () => {
    setError("");
    setSending(true);
    try {
      if (kind === "invoices") {
        await api.post(`/api/invoices/${docId}/send`);
      } else {
        await api.patch(`/api/quotes/${docId}/status`, { status: "SENT" });
      }
      setSuccess("Gemarkeerd als verstuurd (zonder e-mail).");
      onSent?.();
      setTimeout(() => onClose(), 1500);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Markeren mislukt.");
    } finally {
      setSending(false);
    }
  };

  const fromLabel = mailInfo?.configured
    ? `${mailInfo.fromName} <${mailInfo.fromEmail}>`
    : "niet geconfigureerd";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)} e-mailen`}
      subtitle={`${docNumber} — als .html-bijlage + beveiligde online link`}
      size="lg"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-slate-500">
            Van: <span className="font-semibold text-slate-700">{fromLabel}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose} disabled={sending}>
              Annuleren
            </Button>
            {notConfigured ? (
              <Button variant="outline" onClick={handleMarkSentOnly} loading={sending}>
                Toch markeren als verstuurd
              </Button>
            ) : (
              <Button onClick={handleSend} loading={sending} disabled={!mailInfo?.configured}>
                <Send size={20} /> Verstuur
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Not-configured warning */}
        {mailInfo && !mailInfo.configured && (
          <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
            <AlertTriangle size={22} className="shrink-0 mt-0.5" />
            <div className="space-y-1.5">
              <p className="font-semibold">Mail is nog niet geconfigureerd.</p>
              <p>Stel onder Instellingen → Mail je SMTP-gegevens in om facturen/offertes per e-mail te versturen.</p>
              <Button size="sm" variant="outline" onClick={() => navigate("/settings")}>
                <Settings size={16} /> Open Mail-instellingen
              </Button>
            </div>
          </div>
        )}

        {notConfigured && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
            De e-mail kon niet worden verzonden omdat mail niet is geconfigureerd. Je kunt de {typeLabel} toch
            markeren als verstuurd (zonder e-mail) via de knop rechtsonder.
          </div>
        )}

        {error && !notConfigured && (
          <div className="flex items-center gap-2 rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700">
            <AlertTriangle size={20} /> {error}
          </div>
        )}

        {success && (
          <div className="flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-700">
            <CheckCircle2 size={20} /> {success}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4">
          <Input label="Naar" placeholder="klant@domein.nl (komma-gescheiden)" value={to} onChange={(e) => setTo(e.target.value)} icon={<Mail size={18} />} />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Cc" placeholder="optioneel" value={cc} onChange={(e) => setCc(e.target.value)} />
            <Input label="Bcc" placeholder="optioneel" value={bcc} onChange={(e) => setBcc(e.target.value)} />
          </div>
          <Input label="Onderwerp" value={subject} onChange={(e) => setSubject(e.target.value)} />
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Inhoud (concept)</label>
            <textarea
              rows={8}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white/80 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 focus:bg-white transition-all text-sm"
            />
            <p className="mt-1 text-xs text-slate-400">
              De beveiligde online link wordt automatisch onderaan de e-mail toegevoegd.
            </p>
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={attachHtml}
              onChange={(e) => setAttachHtml(e.target.checked)}
              className="w-5 h-5 rounded border-slate-300 text-brand-500 focus:ring-brand-500/30"
            />
            <span className="inline-flex items-center gap-1.5 text-sm text-slate-700">
              <Paperclip size={16} /> {typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)} als HTML-bijlage meesturen
            </span>
          </label>

          <div className="flex items-center gap-2 rounded-xl bg-slate-50 border border-slate-200 px-4 py-2.5 text-xs text-slate-500">
            <FileText size={16} />
            <span>De bijlage opent in de browser en is afdrukbaar naar PDF. De online link is 30 dagen geldig.</span>
          </div>
        </div>
      </div>
    </Modal>
  );
}